/**
 * Authentication utility functions
 */

/**
 * Check if user is authenticated based on WordPress API settings and user data
 * @param {Object} userData - User data from GlobalContext
 * @param {boolean} isLoading - Loading state from GlobalContext
 * @param {string} apiError - API error from GlobalContext
 * @returns {boolean|null} - true if authenticated, false if not, null if still checking
 */
export const isAuthenticated = (userData, isLoading, apiError) => {
  // Check if logout is in progress - if so, don't evaluate authentication
  if (sessionStorage.getItem('dbsb_logout_in_progress')) {
    return null;
  }

  // If still loading, return null to indicate we're still checking
  if (isLoading) {
    return null;
  }

  // Check if we have WordPress API settings (indicates we're in WordPress environment)
  if (!window.wpApiSettings?.nonce) {
    return false;
  }

  // Check if there's an authentication error
  if (apiError && apiError.includes('Authentication token not found')) {
    return false;
  }

  // If we have user data and it's not the default "N/A" values, user is authenticated
  if (userData && userData.email !== 'N/A' && userData.firstName !== 'N/A') {
    return true;
  }

  // If not loading and no valid user data, not authenticated
  return false;
};

/**
 * Get the return URL from query parameters
 * @param {string} search - window.location.search
 * @returns {string|null} - Return URL or null if not found
 */
export const getReturnUrl = (search) => {
  const urlParams = new URLSearchParams(search);
  return urlParams.get('returnUrl');
};

/**
 * Build a URL with return URL parameter
 * @param {string} baseUrl - Base URL to add return URL to
 * @param {string} returnUrl - Return URL to add as parameter
 * @returns {string} - URL with return URL parameter
 */
export const buildUrlWithReturnUrl = (baseUrl, returnUrl) => {
  if (!returnUrl) {
    return baseUrl;
  }
  
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}returnUrl=${encodeURIComponent(returnUrl)}`;
};

/**
 * Check if a route is public (doesn't require authentication)
 * @param {string} pathname - Current pathname
 * @returns {boolean} - true if route is public
 */
export const isPublicRoute = (pathname) => {
  const publicRoutes = [
    '/',
    '/genetic-counselors',
    '/register',
    '/login',
    '/otp',
    '/forgot-password',
    '/new-password'
  ];

  // Check exact matches
  if (publicRoutes.includes(pathname)) {
    return true;
  }

  // Check dynamic routes
  if (pathname.startsWith('/counselor-details/')) {
    return true;
  }

  return false;
};

/**
 * Check if a route requires authentication
 * @param {string} pathname - Current pathname
 * @returns {boolean} - true if route requires authentication
 */
export const isProtectedRoute = (pathname) => {
  return !isPublicRoute(pathname);
};

/**
 * Handle user logout
 * @param {Function} setUserData - Function to update user data in context
 * @param {Function} navigate - Navigation function (optional)
 * @returns {Promise<void>}
 */
export const handleLogout = async (setUserData, navigate = null) => {
  try {
    if (!window.wpApiSettings?.nonce) {
      throw new Error("Authentication token not found.");
    }

    const response = await fetch("/wp-json/dbsb/v1/user/logout", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-WP-Nonce": window.wpApiSettings.nonce,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to log out");
    }

    // Set logout flag to prevent authentication guards from running
    sessionStorage.setItem('dbsb_logout_in_progress', 'true');

    // Clear user data from context first
    setUserData({
      firstName: "N/A",
      lastName: "N/A",
      email: "N/A",
      profilePic: null,
      role: "patient",
      verificationStatus: "Pending",
    });

    // Use a small delay to ensure the loading state is visible
    // before navigation occurs
    setTimeout(() => {
      // Clear any cached data and force navigation
      // Use window.location.replace to avoid adding to browser history
      // Navigate to clean login URL without any return URL parameters
      window.location.replace("/login");
    }, 100);
  } catch (error) {
    console.error("Error logging out:", error);
    // Even if logout fails, clear local state and redirect
    sessionStorage.setItem('dbsb_logout_in_progress', 'true');
    setUserData({
      firstName: "N/A",
      lastName: "N/A",
      email: "N/A",
      profilePic: null,
      role: "patient",
      verificationStatus: "Pending",
    });
    
    // Use a small delay to ensure the loading state is visible
    setTimeout(() => {
      window.location.replace("/login");
    }, 100);
  }
};
